import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogMain,
  Input, 
  Label,
  toast 
} from '@ncoder/ui';
import { Edit, Loader2 } from 'lucide-react';
import { useUpdateOrg } from '@/hooks/org/useUpdateOrg';

interface EditConnectionDialogProps {
  orgId: string;
  currentName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditConnectionDialog: React.FC<EditConnectionDialogProps> = ({
  orgId,
  currentName,
  isOpen,
  onOpenChange,
}) => {
  const [orgName, setOrgName] = useState(currentName);
  const updateOrg = useUpdateOrg();

  // Reset form when dialog opens with current name
  useEffect(() => {
    if (isOpen) {
      setOrgName(currentName);
    }
  }, [isOpen, currentName]);

  const handleSave = () => {
    if (!orgName.trim()) {
      toast({
        title: "Error",
        description: "Organization name cannot be empty"
      });
      return;
    }

    updateOrg.mutate(
      {
        id: orgId,
        orgName: orgName.trim(),
      },
      {
        onSuccess: (data) => {
          toast({
            title: "Success",
            description: `Organization name updated to "${data.orgName}"`,
          });
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message || "Failed to update organization"
          });
        }
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Organization
          </DialogTitle>
          <DialogDescription>
            Update the name of your Salesforce organization
          </DialogDescription>
        </DialogHeader>
        
        <DialogMain className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Organization Name</Label>
            <Input 
              id="org-name" 
              placeholder="My Salesforce Org"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              disabled={updateOrg.isPending}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={updateOrg.isPending}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={updateOrg.isPending}
            >
              {updateOrg.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </DialogMain>
      </DialogContent>
    </Dialog>
  );
};

